import { motion } from "motion/react";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { AgentChatPanel } from "@/components/AgentChatPanel";
import { useAgentChat } from "@/components/AgentChatProvider";
import { useMediaQuery } from "@/hooks/useMediaQuery";

/** Only one layout is ever mounted: hiding the other with CSS fails (the resizable group's inline `display` beats `hidden`). */
export function AgentChatLayout({ children }: { children: React.ReactNode }) {
  const { isOpen, agentName, closeChat } = useAgentChat();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { pathname } = useLocation();

  // On mobile the chat covers the page, so when the agent (or anything else) navigates,
  // close it to show where the user was taken.
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (pathname === lastPath.current) return;
    lastPath.current = pathname;
    if (!isDesktop && isOpen) closeChat();
  }, [pathname, isDesktop, isOpen, closeChat]);

  const page = <div className="@container min-h-0 flex-1 overflow-y-auto">{children}</div>;

  if (!isOpen) return page;

  if (!isDesktop) {
    return (
      <>
        {/* Kept mounted underneath so the page keeps its state while the chat is up. */}
        {page}
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${agentName} Agent`}
          className="fixed inset-0 z-[60] flex flex-col bg-background pb-[env(safe-area-inset-bottom)]"
        >
          <AnimatedChatPanel />
        </div>
      </>
    );
  }

  return (
    <ResizablePanelGroup orientation="horizontal" className="min-h-0 flex-1">
      <ResizablePanel defaultSize="45%" minSize="25%" maxSize="60%" className="overflow-hidden">
        <AnimatedChatPanel />
      </ResizablePanel>
      <ResizableHandle withHandle />
      <ResizablePanel defaultSize="55%" minSize="30%" className="@container overflow-y-auto">
        {children}
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}

function AnimatedChatPanel() {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <motion.div
      data-sandbox-chat
      tabIndex={-1}
      role="region"
      aria-label="Agent conversation"
      className="h-full min-h-0 outline-none"
      initial={reducedMotion ? false : { opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: reducedMotion ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      <AgentChatPanel />
    </motion.div>
  );
}
